import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  BlockAddRow,
  BlockDeskError,
  BlockLine,
  BlockListEmpty,
  type BlockTone,
} from '@/features/quotes/components/QuoteBlockDesk'
import { useBlockDesk } from '@/features/quotes/components/useBlockDesk'
import { cn } from '@/lib/cn'
import type { BlockDesk } from '@/features/quotes/components/useBlockDesk'
import type { QuoteBlockKind, QuoteDetail } from '@/types/domain'

/* What the price covers and what it does not (Story 5.2, FR-60).
 *
 * These print on the offer — the inclusions as one list, the exclusions as
 * another after the money, verbatim — so this is the screen where the words a
 * client will hold this company to are actually typed. Before it existed the
 * endpoints were reachable and nothing reached them, and an offer could only
 * ever print empty blocks.
 *
 * ── Why the two lists are side by side, and tinted ──────────────────────────
 *
 * They are ONE question answered twice: the same list with the sign flipped,
 * and the way anybody checks it is by reading them against each other. Stacked,
 * the second was below the fold of the card and the pair could not be compared
 * at all. The tint is the reference quotation's — green for what is covered,
 * red for what is not — and it is doing the work a heading alone was doing
 * badly: on a document this pair is read at a glance, and a reader who mistakes
 * one list for the other has misread the price.
 *
 * The lists are in the order the OFFER prints them — offer.go composes the
 * included block immediately before the not-included one — because another
 * order here would make this card a rehearsal for a document nobody could read
 * it against.
 *
 * ── The price note is no longer typed here ──────────────────────────────────
 *
 * FR-60 attaches an optional price or tariff text to exclusions alone, and the
 * form sheet that asked for it separately is gone: one line per entry now. A
 * stored one still PRINTS and still shows on its row, because the composer joins
 * body and price with an em dash — which is exactly what "Customs duties — as
 * per tariff" typed as one line produces. What was lost is the separate field,
 * not the ability to say it.
 *
 * Everything else — why the kind cannot change, why the whole card is read-only
 * past draft, where the label comes from — is answered in `useBlockDesk`.
 */
export function QuoteBlocksCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const desk = useBlockDesk(record)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotes.blocks.title')}</CardTitle>
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {desk.readOnly ? t('quotes.blocks.frozen') : t('quotes.blocks.hint')}
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pt-2">
        <BlockDeskError desk={desk} />

        {desk.loading ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : desk.failedToLoad ? (
          <p role="alert" className="text-negative text-[12.5px] leading-snug">
            {t('quotes.blocks.loadFailed')}
          </p>
        ) : (
          <div className="grid gap-3.5 lg:grid-cols-2">
            <BlockPanel
              desk={desk}
              kind="included"
              tone="positive"
              heading={t('quotes.blocks.included')}
              empty={t('quotes.blocks.includedEmpty')}
              placeholder={t('quotes.blocks.addIncludedPlaceholder')}
              addLabel={t('quotes.blocks.addIncluded')}
              editLabel={t('quotes.blocks.editIncluded')}
              deleteLabel={t('quotes.blocks.removeIncluded')}
            />
            <BlockPanel
              desk={desk}
              kind="not-included"
              tone="negative"
              heading={t('quotes.blocks.notIncluded')}
              empty={t('quotes.blocks.notIncludedEmpty')}
              placeholder={t('quotes.blocks.addNotIncludedPlaceholder')}
              addLabel={t('quotes.blocks.addNotIncluded')}
              editLabel={t('quotes.blocks.editNotIncluded')}
              deleteLabel={t('quotes.blocks.removeNotIncluded')}
            />
          </div>
        )}
      </CardBody>
    </Card>
  )
}

const panelTone: Record<'positive' | 'negative', { frame: string; heading: string }> = {
  positive: { frame: 'border-positive/25 bg-positive-soft/45', heading: 'text-positive' },
  negative: { frame: 'border-negative/25 bg-negative-soft/45', heading: 'text-negative' },
}

/** One tinted half. A `section` with its own heading, so the two halves are two
 *  landmarks rather than one list a reader has to split by colour. */
function BlockPanel({
  desk,
  kind,
  tone,
  heading,
  empty,
  placeholder,
  addLabel,
  editLabel,
  deleteLabel,
}: {
  desk: BlockDesk
  kind: QuoteBlockKind
  tone: 'positive' | 'negative'
  heading: string
  empty: string
  placeholder: string
  addLabel: string
  editLabel: string
  deleteLabel: string
}) {
  const blocks = desk.of(kind)

  return (
    <section
      className={cn(
        'flex flex-col gap-1 rounded-[var(--radius-lg)] border px-3.5 py-3',
        panelTone[tone].frame,
      )}
    >
      <h3 className={cn('text-[12.5px] leading-none font-semibold', panelTone[tone].heading)}>
        {heading}
      </h3>

      {blocks.length === 0 ? (
        <BlockListEmpty>{empty}</BlockListEmpty>
      ) : (
        <ul className="flex flex-col">
          {blocks.map((block) => (
            <BlockLine
              key={block.id}
              block={block}
              desk={desk}
              editLabel={editLabel}
              deleteLabel={deleteLabel}
            />
          ))}
        </ul>
      )}

      {desk.readOnly ? null : (
        <BlockAddRow
          desk={desk}
          kind={kind}
          tone={tone as BlockTone}
          compact
          placeholder={placeholder}
          actionLabel={addLabel}
        />
      )}
    </section>
  )
}
